import { COMMUNICATION_MOBILE_QUERY, isImageWidthPercent, youtubeEmbedUrl, youtubeVideoId } from '../../../src/features/comunicacoes/media'

declare const __PQLFLOW_SDK_API_URL__: string
declare const __PQLFLOW_SDK_VERSION__: string

export {}

type IdentityProvider = () => string | Promise<string>
type Command = ['init', InitOptions] | ['identify', IdentifyOptions] | ['destroy']
type EventType = 'VISUALIZOU' | 'CLICOU' | 'CONFIRMOU' | 'DISPENSOU'
type AnswerValue = string | number

interface InitOptions {
  integrationId: string
  getIdentityToken: IdentityProvider
}

interface IdentifyOptions {
  getIdentityToken: IdentityProvider
}

interface SdkSession {
  token: string
  expiresAt: string
  serverTime: string
  pollIntervalSeconds: number
}

interface ImageContent {
  larguraPercentual?: number
  url: string
  alt: string
}

interface ButtonContent {
  texto: string
  url: string
}

interface InterestButtonContent {
  acaoId: string
  texto: string
  mensagemSucesso: string
}

type PresentationIcon = 'CARTAO' | 'CALENDARIO' | 'ESCUDO' | 'PRESENTE' | 'INFORMACAO'

interface PresentationContent {
  cabecalho: { legenda: string; icone: PresentationIcon | null; corInicial: string; corFinal: string } | null
  destaque: { titulo: string; texto: string; icone: PresentationIcon | null; cor: string } | null
  cartoes: Array<{ id: string; titulo: string; texto: string; icone: PresentationIcon | null; corFundo: string }>
  textoFinal: string | null
  botaoSecundario: string | null
  botaoPrincipal: string | null
}

interface QuestionBase {
  id: string
  titulo: string
  obrigatoria: boolean
}

interface TextQuestion extends QuestionBase {
  tipo: 'TEXTO'
}

interface ScoreQuestion extends QuestionBase {
  tipo: 'NOTA'
}

interface ChoiceQuestion extends QuestionBase {
  tipo: 'ESCOLHA_UNICA'
  opcoes: Array<{ id: string; texto: string }>
}

type Question = TextQuestion | ScoreQuestion | ChoiceQuestion

interface NoticeContent {
  exibirNoMobile?: boolean
  video?: { provedor: 'YOUTUBE'; url: string } | null
  formato: 'BANNER' | 'MODAL'
  titulo: string
  texto: string
  corFundo: string
  corTexto: string
  corDestaque: string
  imagem: ImageContent | null
  botao: ButtonContent | null
  botaoInteresse: InterestButtonContent | null
  apresentacao?: PresentationContent | null
}

interface SurveyContent {
  exibirNoMobile?: boolean
  formato: 'MODAL'
  titulo: string
  texto: string
  corFundo: string
  corTexto: string
  corDestaque: string
  agradecimento: string
  perguntas: Question[]
  apresentacao?: PresentationContent | null
}

interface CampaignBase {
  id: string
  version: number
  inicioEm: string
  fimEm: string
  entregaId: string
  entregaExpiresAt: string
}

type Campaign = (CampaignBase & { tipo: 'AVISO'; conteudo: NoticeContent }) | (CampaignBase & { tipo: 'PESQUISA'; conteudo: SurveyContent })

interface PendingResponse {
  items: Campaign[]
  serverTime: string
  pollIntervalSeconds: number
}

interface InterestConfirmation {
  id: string
  campanhaId: string
  idFilial: string | null
  acaoId: string
  receivedAt: string
}

interface PQLFlowDispatcher {
  (...command: Command): void
  q?: unknown[]
  version?: string
}

declare global {
  interface Window {
    PQLFlow?: PQLFlowDispatcher
    __PQLFLOW_SDK_INSTANCE__?: PQLFlowRuntime
  }
}

const API_URL = __PQLFLOW_SDK_API_URL__.replace(/\/$/, '')
const DEFAULT_POLL_MS = 60_000
const REQUEST_TIMEOUT_MS = 8_000
const MAX_BACKOFF_MS = 120_000
const SESSION_RENEWAL_MARGIN_MS = 30_000
const CHANNEL_PREFIX = 'pqlflow:'

const styles = `
:host{all:initial;color-scheme:light;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;line-height:1.45}
*,*::before,*::after{box-sizing:border-box}
.overlay{position:fixed;inset:0;z-index:2147483646;display:flex;align-items:flex-start;justify-content:center;overflow-y:auto;padding:24px;background:rgba(15,23,42,.52)}
.card{position:relative;flex:none;width:min(560px,100%);max-height:none;margin:auto;overflow:hidden;border:1px solid rgba(15,23,42,.12);border-radius:16px;padding:24px;background:var(--pql-bg,#fff);color:var(--pql-text,#172033);box-shadow:0 24px 60px rgba(15,23,42,.24);font-size:16px}
.banner{position:fixed;z-index:2147483646;top:16px;right:16px;left:16px;display:flex;align-items:center;gap:16px;max-width:1120px;margin:auto;border:1px solid rgba(15,23,42,.12);border-radius:14px;padding:16px 52px 16px 18px;background:var(--pql-bg,#fff);color:var(--pql-text,#172033);box-shadow:0 12px 36px rgba(15,23,42,.2);font-size:16px}
.body{flex:1;min-width:0}.title{margin:0 0 6px;font-size:1.2em;line-height:1.25}.text{margin:0;white-space:pre-wrap}.image{display:block;width:100%;max-height:220px;object-fit:cover;border-radius:10px;margin:0 0 16px}.banner .image{width:88px;height:64px;margin:0;flex:none}.actions{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:10px;margin-top:20px}.banner .actions{margin:0;flex:none}
.button{appearance:none;border:1px solid transparent;border-radius:9px;padding:10px 16px;background:var(--pql-accent,#175cd3);color:#fff;font:600 14px/1.2 inherit;cursor:pointer}.button:disabled{cursor:wait;opacity:.68}.button:focus-visible,.close:focus-visible,input:focus-visible,textarea:focus-visible{outline:3px solid color-mix(in srgb,var(--pql-accent,#175cd3) 48%,white);outline-offset:2px}.button.secondary{border-color:currentColor;background:transparent;color:var(--pql-text,#172033)}
.close{position:absolute;top:10px;right:10px;width:36px;height:36px;border:0;border-radius:50%;background:transparent;color:inherit;font:24px/1 inherit;cursor:pointer}.close:disabled{cursor:wait;opacity:.5}.field{display:block;margin-top:18px}.label{display:block;margin-bottom:7px;font-weight:650}.required{color:#b42318}.input,.textarea{width:100%;border:1px solid #98a2b3;border-radius:8px;padding:10px 12px;background:#fff;color:#101828;font:inherit}.textarea{min-height:90px;resize:vertical}.choices{display:grid;gap:8px}.choice{display:flex;align-items:flex-start;gap:8px}.scores{display:flex;flex-wrap:wrap;gap:7px}.score{position:relative}.score input{position:absolute;opacity:0;pointer-events:none}.score span{display:grid;width:38px;height:38px;place-items:center;border:1px solid #98a2b3;border-radius:8px;background:#fff;color:#101828;cursor:pointer}.score input:checked+span{border-color:var(--pql-accent,#175cd3);background:var(--pql-accent,#175cd3);color:#fff}.error{margin:6px 0 0;color:#b42318;font-size:14px}.status{min-height:21px;margin:12px 0 0;font-size:14px}.status.error{color:#b42318}.status.success{color:#067647;font-weight:650}.thanks{text-align:center;padding:28px 8px}.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
@media(max-width:680px){.overlay{padding:16px 8px}.card{width:100%;max-height:none;border-radius:16px;padding:22px 18px}.banner{top:8px;right:8px;left:8px;align-items:flex-start;flex-wrap:wrap;padding:16px 48px 16px 16px}.banner .image{width:64px;height:48px}.banner .actions{width:100%;justify-content:stretch}.banner .button{flex:1}}
.card.notice-card,.card.survey-card{width:min(760px,100%);padding:0;overflow:hidden;border-radius:16px;background:var(--pql-bg,#fff);box-shadow:0 24px 64px rgba(23,32,52,.28)}
.banner.notice-banner{display:block;max-width:980px;padding:0;overflow:hidden;border-radius:14px;background:var(--pql-bg,#fff)}
.pql-header{position:relative;display:flex;align-items:center;gap:16px;min-height:98px;padding:22px 58px 22px 24px;overflow:hidden;color:#fff;background:linear-gradient(125deg,var(--pql-header-start,#2f5376),var(--pql-header-end,#5941a9))}
.pql-header::after{content:"";position:absolute;width:176px;height:176px;right:-52px;top:-104px;border:24px solid rgba(255,255,255,.08);border-radius:50%;pointer-events:none}
.pql-header-icon{position:relative;z-index:1;display:grid;place-items:center;flex:none;width:48px;height:48px;border-radius:14px;background:rgba(255,255,255,.16)}
.pql-header-icon svg{width:24px;height:24px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.pql-header-copy{position:relative;z-index:1;min-width:0}.pql-eyebrow{display:block;margin-bottom:4px;font-size:11px;font-weight:750;letter-spacing:.06em;text-transform:uppercase}.pql-header .title{margin:0;font-size:clamp(18px,2.5vw,23px);line-height:1.2;font-weight:750}
.pql-main{padding:24px}.pql-main>.text{margin:0 0 18px;padding:14px 18px;border-left:4px solid var(--pql-accent,#55789c);border-radius:0 12px 12px 0;background:linear-gradient(90deg,color-mix(in srgb,var(--pql-accent,#55789c) 8%,var(--pql-bg,#fff)),transparent);font-family:"Segoe UI",system-ui,sans-serif;font-size:17px;font-weight:500;line-height:1.6}.pql-main>.text:last-child{margin-bottom:0}.pql-main>.image{margin:0 0 18px;max-height:240px}.pql-footer{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:10px;padding:16px 24px;border-top:1px solid rgba(47,83,118,.13)}.pql-footer .actions{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:10px;margin:0}.pql-footer .button{min-height:44px}.pql-footer .button.secondary{border-color:color-mix(in srgb,var(--pql-accent,#2f5376) 42%,white);color:var(--pql-accent,#2f5376)}
.notice-card>.close,.survey-card>.close,.notice-banner>.close{z-index:2;top:14px;right:14px;color:#fff;background:rgba(255,255,255,.12)}.notice-card>.close:hover,.survey-card>.close:hover,.notice-banner>.close:hover{background:rgba(255,255,255,.24)}
.notice-banner .pql-header{min-height:72px;padding:12px 58px 12px 18px}.notice-banner .pql-header-icon{width:38px;height:38px;border-radius:10px}.notice-banner .pql-header-icon svg{width:20px;height:20px}.notice-banner .pql-header .title{font-size:18px}.notice-banner .pql-main{padding:16px 18px}.notice-banner .pql-main>.text{font-size:16px}.notice-banner .pql-footer{padding:12px 18px}.notice-banner .pql-main>.image{width:100%;height:auto;max-height:150px}
.survey-card form{display:block}.survey-card .pql-footer .status{flex:1;min-width:180px;margin:0;align-self:center}
.survey-questions{display:grid;gap:14px;margin-top:20px}.question-card{min-width:0;padding:18px;border:1px solid #d7e1ef;border-radius:13px;background:color-mix(in srgb,var(--pql-bg,#fff) 70%,#fff);box-shadow:0 4px 16px rgba(47,83,118,.045)}.question-card .field{min-width:0;margin:0;padding:0;border:0}.question-card .label{width:100%;margin:0 0 14px;padding:0;color:var(--pql-text,#172033);font-size:16px;font-weight:700;line-height:1.4}.question-heading{display:flex;align-items:center;gap:10px}.question-number{display:inline-grid;place-items:center;flex:none;min-width:30px;height:30px;padding:0 5px;border-radius:9px;background:color-mix(in srgb,var(--pql-accent,#175cd3) 11%,white);color:var(--pql-accent,#175cd3);font-size:12px;font-weight:800}.question-title{min-width:0;overflow-wrap:anywhere}.question-card .required{margin-left:4px}.question-optional{margin-left:7px;color:#667c90;font-size:12px;font-weight:500}.question-card .textarea{min-height:100px;padding:12px 14px;border:1px solid #c8d7e6;border-radius:10px;background:#fff;color:var(--pql-text,#172033);line-height:1.55}.question-card .textarea:focus{border-color:var(--pql-accent,#175cd3)}.question-card .choices{gap:9px}.question-card .choice{min-height:44px;align-items:center;gap:11px;padding:10px 13px;border:1px solid #d7e1ef;border-radius:10px;background:#fff;cursor:pointer;line-height:1.35}.question-card .choice:hover{border-color:color-mix(in srgb,var(--pql-accent,#175cd3) 45%,#d7e1ef)}.question-card .choice:has(input:checked){border-color:var(--pql-accent,#175cd3);background:color-mix(in srgb,var(--pql-accent,#175cd3) 8%,white)}.question-card .choice:has(input:focus-visible){outline:3px solid color-mix(in srgb,var(--pql-accent,#175cd3) 48%,white);outline-offset:2px}.question-card .choice input{flex:none;width:17px;height:17px;margin:0;accent-color:var(--pql-accent,#175cd3)}.question-card .scores{display:grid;grid-template-columns:repeat(11,minmax(0,1fr));gap:7px}.question-card .score{min-width:0}.question-card .score span{width:100%;min-height:44px;border-color:#c8d7e6;border-radius:9px;font-size:14px;font-weight:700}.question-card .score:hover span{border-color:var(--pql-accent,#175cd3)}.question-card .score input:focus-visible+span{outline:3px solid color-mix(in srgb,var(--pql-accent,#175cd3) 48%,white);outline-offset:2px}
.pql-plain-title{display:block;margin:0 0 12px;font-size:22px;line-height:1.25}.pql-highlight{display:flex;gap:14px;margin-top:20px;padding:16px;color:var(--pql-highlight-color,#d5615a);background:color-mix(in srgb,var(--pql-highlight-color,#d5615a) 8%,white);border:1px solid color-mix(in srgb,var(--pql-highlight-color,#d5615a) 35%,white);border-left:5px solid var(--pql-highlight-color,#d5615a);border-radius:12px}.pql-highlight strong,.pql-detail strong{display:block;font-size:15px}.pql-highlight p,.pql-detail p{margin:5px 0 0;font-size:14px;line-height:1.5;white-space:pre-wrap}
.pql-detail-icon{display:grid;place-items:center;flex:none;width:36px;height:36px;color:#2f5376;background:#e3ebf5;border-radius:10px}.pql-detail-icon svg{width:19px;height:19px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}.pql-highlight .pql-detail-icon{color:var(--pql-highlight-color,#d5615a);background:color-mix(in srgb,var(--pql-highlight-color,#d5615a) 14%,white)}.pql-details{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin-top:16px}.pql-detail{display:flex;gap:12px;min-width:0;padding:16px;color:#2f5376;background:#f4f7fd;border:1px solid #d7e1ef;border-radius:12px}.pql-closing{margin:20px 0 0;padding-top:16px;border-top:1px solid color-mix(in srgb,var(--pql-accent,#55789c) 22%,transparent);font-family:"Segoe UI",system-ui,sans-serif;font-size:16px;font-weight:600;line-height:1.6;white-space:pre-wrap}
.pql-header .pql-header-icon{width:48px;height:48px;color:#fff;background:rgba(255,255,255,.16)}.notice-banner .pql-header .pql-header-icon{width:38px;height:38px}.no-header>.close{color:var(--pql-text,#172033);background:rgba(47,83,118,.08)}.no-header>.close:hover{background:rgba(47,83,118,.16)}.no-header>.pql-main{padding-top:42px}
@media(max-width:680px){.card.notice-card,.card.survey-card{width:100%;max-height:none;border-radius:16px}.notice-banner{top:8px;right:8px;left:8px}.pql-header{min-height:78px;padding:18px 54px 18px 16px;gap:12px}.pql-header-icon{width:42px;height:42px}.pql-header .title{font-size:18px}.pql-main{padding:16px}.pql-footer{padding:14px 16px}.pql-footer .actions{width:100%}.pql-footer .button{flex:1}.notice-banner .pql-main{padding:14px 16px}.notice-banner .pql-footer{padding:12px 16px}.question-card{padding:14px}.question-card .scores{grid-template-columns:repeat(auto-fit,minmax(44px,1fr))}}
@media(max-width:680px){.pql-details{grid-template-columns:1fr}.pql-highlight,.pql-detail{padding:13px}}
@media(max-width:575px){.pql-footer .actions{flex-direction:column}.pql-footer .button{width:100%;text-align:center}}
.card.notice-card .pql-main>.image{height:auto;max-height:none;object-fit:contain}.pql-video{width:100%;aspect-ratio:16/9;margin:18px 0;overflow:hidden;border-radius:12px;background:#102a43}.pql-video iframe{display:block;width:100%;height:100%;border:0}
@media(prefers-reduced-motion:no-preference){.card,.banner{animation:pql-in .18s ease-out}@keyframes pql-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}}
`

class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly retryAfterMs: number | null
  ) {
    super(`PQLFlow SDK request failed with status ${status}`)
  }
}

class PQLFlowRuntime {
  private integrationId: string | null = null
  private identityProvider: IdentityProvider | null = null
  private session: SdkSession | null = null
  private currentCampaign: Campaign | null = null
  private host: HTMLElement | null = null
  private pollTimer: number | null = null
  private expiryTimer: number | null = null
  private generation = 0
  private failureCount = 0
  private serverOffsetMs = 0
  private previousFocus: HTMLElement | null = null
  private controllers = new Set<AbortController>()
  private eventKeys = new Map<string, string>()
  private responseAttempt: { signature: string; key: string } | null = null
  private interestAttempt: { deliveryId: string; actionId: string; key: string } | null = null
  private interestSubmittingDeliveryId: string | null = null
  private channel: BroadcastChannel | null = null
  private initialized = false
  private listeningForVisibility = false
  private mobileViewport = window.matchMedia(COMMUNICATION_MOBILE_QUERY)

  constructor() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.mobileViewport.addEventListener('change', this.handleDeviceChange)
    this.listeningForVisibility = true
  }

  dispatch(command: unknown, options?: unknown) {
    if (command === 'init' && isInitOptions(options)) void this.init(options)
    if (command === 'identify' && isIdentifyOptions(options)) void this.identify(options)
    if (command === 'destroy') this.destroy()
  }

  async init(options: InitOptions) {
    const sameContext = this.initialized && this.integrationId === options.integrationId && this.identityProvider === options.getIdentityToken
    if (sameContext) return

    this.resetContext()
    this.initialized = true
    if (!this.listeningForVisibility) {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
      this.mobileViewport.addEventListener('change', this.handleDeviceChange)
      this.listeningForVisibility = true
    }
    this.integrationId = options.integrationId.trim()
    this.identityProvider = options.getIdentityToken
    this.openChannel()
    const generation = this.generation
    await this.start(generation)
  }

  async identify(options: IdentifyOptions) {
    if (!this.initialized || !this.integrationId) return
    this.resetContext(false)
    this.identityProvider = options.getIdentityToken
    this.openChannel()
    const generation = this.generation
    await this.start(generation)
  }

  destroy() {
    this.resetContext()
    this.initialized = false
    this.integrationId = null
    this.identityProvider = null
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.mobileViewport.removeEventListener('change', this.handleDeviceChange)
    this.listeningForVisibility = false
  }

  private async start(generation: number) {
    if (document.visibilityState === 'hidden') return
    try {
      await this.ensureSession(generation)
      if (generation === this.generation) await this.poll(generation)
    } catch (error) {
      if (generation === this.generation && !isAbortError(error)) this.scheduleAfterFailure(error, generation)
    }
  }

  private async ensureSession(generation: number, force = false) {
    if (!this.integrationId || !this.identityProvider) throw new Error('PQLFlow SDK is not initialized')
    if (!force && this.session && Date.parse(this.session.expiresAt) - this.serverNow() > SESSION_RENEWAL_MARGIN_MS) return

    const identityToken = await this.identityProvider()
    if (generation !== this.generation) throw new DOMException('Stale context', 'AbortError')
    if (typeof identityToken !== 'string' || !identityToken.trim()) throw new Error('Identity provider returned an invalid token')

    const payload = await this.request<unknown>('/sessoes', {
      method: 'POST',
      body: JSON.stringify({ integrationId: this.integrationId, identityToken, sdkVersion: __PQLFLOW_SDK_VERSION__, capacidades: ['INTERESSE_V1'] })
    })
    if (generation !== this.generation) throw new DOMException('Stale context', 'AbortError')
    this.session = parseSession(payload)
    this.updateServerClock(this.session.serverTime)
  }

  private async poll(generation: number, renewed = false) {
    if (generation !== this.generation || document.visibilityState === 'hidden') return

    try {
      await this.ensureSession(generation)
      const query = new URLSearchParams({ dispositivo: this.mobileViewport.matches ? 'MOBILE' : 'DESKTOP' })
      if (this.currentCampaign) query.set('entregaAtualId', this.currentCampaign.entregaId)
      const payload = await this.request<unknown>(`/campanhas/pendentes?${query}`, { headers: this.authHeaders() })
      if (generation !== this.generation) return
      const response = parsePendingResponse(payload)
      this.updateServerClock(response.serverTime)
      this.failureCount = 0
      this.applyCampaign(response.items[0] ?? null, generation)
      this.schedulePoll(clampPoll(response.pollIntervalSeconds), generation)
    } catch (error) {
      if (generation !== this.generation || isAbortError(error)) return
      if (error instanceof HttpError && error.status === 401 && !renewed) {
        this.session = null
        try {
          await this.ensureSession(generation, true)
          await this.poll(generation, true)
          return
        } catch (renewalError) {
          if (generation !== this.generation || isAbortError(renewalError)) return
          this.scheduleAfterFailure(renewalError, generation)
          return
        }
      }
      if (error instanceof HttpError && error.status === 403) {
        this.closeCampaign(false)
        return
      }
      this.scheduleAfterFailure(error, generation)
    }
  }

  private applyCampaign(campaign: Campaign | null, generation: number) {
    if (!campaign || (this.mobileViewport.matches && campaign.conteudo.exibirNoMobile === false)) {
      this.closeCampaign(false)
      return
    }
    const current = this.currentCampaign
    if (current && current.id === campaign.id && current.version === campaign.version) {
      this.currentCampaign = campaign
      this.scheduleExpiry(campaign, generation)
      return
    }
    if (current && current.tipo === 'PESQUISA' && this.host?.shadowRoot?.querySelector('form')) return
    this.closeCampaign(false)
    this.currentCampaign = campaign
    this.renderCampaign(campaign, generation)
    this.scheduleExpiry(campaign, generation)
  }

  private renderCampaign(campaign: Campaign, generation: number) {
    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    this.host = document.createElement('div')
    this.host.id = 'pqlflow-communications'
    this.host.dataset.version = __PQLFLOW_SDK_VERSION__
    const root = this.host.attachShadow({ mode: 'open' })
    const style = document.createElement('style')
    style.textContent = styles
    root.append(style)
    document.body.append(this.host)

    if (campaign.tipo === 'AVISO') this.renderNotice(root, campaign, generation)
    else this.renderSurvey(root, campaign, generation)

    requestAnimationFrame(() => {
      if (
        generation === this.generation &&
        this.currentCampaign?.entregaId === campaign.entregaId &&
        document.visibilityState === 'visible' &&
        this.host?.isConnected &&
        !(this.mobileViewport.matches && campaign.conteudo.exibirNoMobile === false)
      ) {
        void this.sendEvent(campaign, 'VISUALIZOU', false)
      }
    })
  }

  private renderNotice(root: ShadowRoot, campaign: Extract<Campaign, { tipo: 'AVISO' }>, generation: number) {
    const content = campaign.conteudo
    const container = element('section', content.formato === 'BANNER' ? 'banner notice-banner' : 'card notice-card')
    setTheme(container, content)
    container.setAttribute('role', content.formato === 'MODAL' ? 'dialog' : 'region')
    container.setAttribute('aria-labelledby', 'pql-campaign-title')
    if (content.texto) container.setAttribute('aria-describedby', 'pql-campaign-description')
    if (content.formato === 'MODAL') container.setAttribute('aria-modal', 'true')

    const heading = createPresentationHeader(content.titulo, 'Aviso', 'notice', content.apresentacao)
    if (!heading) container.classList.add('no-header')
    const body = element('div', 'pql-main')
    if (!heading) {
      const title = element('h2', 'pql-plain-title', content.titulo)
      title.id = 'pql-campaign-title'
      body.append(title)
    }
    if (content.imagem && isHttpsUrl(content.imagem.url)) body.append(createImage(content.imagem))
    if (content.video && content.formato === 'MODAL') {
      const src = youtubeEmbedUrl(content.video.url)
      if (src) {
        const video = element('div', 'pql-video')
        const frame = element('iframe', '')
        frame.src = src
        frame.title = `Vídeo: ${content.titulo}`
        frame.loading = 'lazy'
        frame.allow = 'encrypted-media; picture-in-picture; fullscreen'
        frame.allowFullscreen = true
        frame.referrerPolicy = 'strict-origin-when-cross-origin'
        video.append(frame)
        body.append(video)
      }
    }
    if (content.texto) {
      const description = element('p', 'text', content.texto)
      description.id = 'pql-campaign-description'
      body.append(description)
    }
    appendPresentationSections(body, content.apresentacao)
    const close = this.createCloseButton(campaign)

    const actions = element('div', 'actions')
    if (content.botao && isHttpsUrl(content.botao.url)) {
      const link = element('a', 'button', content.apresentacao?.botaoPrincipal || content.botao.texto)
      link.href = content.botao.url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.addEventListener('click', () => {
        void this.sendEvent(campaign, 'CLICOU', true)
        this.completeCampaign(campaign)
      })
      actions.append(link)
    }
    if (content.botaoInteresse) {
      const status = element('p', 'status')
      status.setAttribute('role', 'status')
      status.setAttribute('aria-live', 'polite')
      status.setAttribute('aria-atomic', 'true')
      const interest = element('button', 'button', content.apresentacao?.botaoPrincipal || content.botaoInteresse.texto)
      interest.type = 'button'
      interest.addEventListener('click', () => void this.submitInterest(campaign, interest, status))
      actions.append(interest)
      body.append(status)
    } else if (!content.botao || !isHttpsUrl(content.botao.url)) {
      const confirm = element('button', 'button', content.apresentacao?.botaoPrincipal || 'Entendi')
      confirm.type = 'button'
      confirm.addEventListener('click', () => {
        void this.sendEvent(campaign, 'CONFIRMOU', true)
        this.completeCampaign(campaign)
      })
      actions.append(confirm)
    }
    const deferText = content.apresentacao ? content.apresentacao.botaoSecundario : 'Fazer depois'
    if (deferText && (content.apresentacao || (content.formato === 'MODAL' && (content.botao || content.botaoInteresse)))) {
      actions.prepend(this.createDeferredButton(campaign, deferText))
    }
    const footer = element('footer', 'pql-footer')
    footer.append(actions)
    if (heading) container.append(heading)
    container.append(body, footer, close)

    const mounted = content.formato === 'MODAL' ? element('div', 'overlay') : container
    if (content.formato === 'MODAL') mounted.append(container)
    root.append(mounted)
    this.activateKeyboard(container, content.formato === 'MODAL', campaign)
    if (generation === this.generation) (content.formato === 'MODAL' ? close : container).focus?.()
  }

  private renderSurvey(root: ShadowRoot, campaign: Extract<Campaign, { tipo: 'PESQUISA' }>, generation: number) {
    const content = campaign.conteudo
    const overlay = element('div', 'overlay')
    const card = element('section', 'card survey-card')
    setTheme(card, content)
    card.setAttribute('role', 'dialog')
    card.setAttribute('aria-modal', 'true')
    card.setAttribute('aria-labelledby', 'pql-campaign-title')
    if (content.texto) card.setAttribute('aria-describedby', 'pql-campaign-description')
    const close = this.createCloseButton(campaign)
    const heading = createPresentationHeader(content.titulo, 'Pesquisa', 'survey', content.apresentacao)
    if (!heading) card.classList.add('no-header')
    const main = element('div', 'pql-main')
    if (!heading) {
      const title = element('h2', 'pql-plain-title', content.titulo)
      title.id = 'pql-campaign-title'
      main.append(title)
    }
    if (content.texto) {
      const description = element('p', 'text', content.texto)
      description.id = 'pql-campaign-description'
      main.append(description)
    }
    appendPresentationSections(main, content.apresentacao)
    const form = document.createElement('form')
    form.noValidate = true

    const questions = element('div', 'survey-questions')
    content.perguntas.forEach((question, index) => questions.append(this.renderQuestion(question, index)))
    main.append(questions)
    const status = element('p', 'status')
    status.setAttribute('role', 'status')
    status.setAttribute('aria-live', 'polite')
    const footer = element('footer', 'pql-footer')
    const actions = element('div', 'actions')
    const submit = element('button', 'button', content.apresentacao?.botaoPrincipal || 'Enviar respostas')
    submit.type = 'submit'
    const surveyDeferText = content.apresentacao ? content.apresentacao.botaoSecundario : 'Fazer depois'
    if (surveyDeferText) actions.append(this.createDeferredButton(campaign, surveyDeferText))
    actions.append(submit)
    footer.append(status, actions)
    form.append(main, footer)
    form.addEventListener('submit', event => void this.submitSurvey(event, campaign, form, status, submit))
    if (heading) card.append(heading)
    card.append(close, form)
    overlay.append(card)
    root.append(overlay)
    this.activateKeyboard(card, true, campaign)
    if (generation === this.generation) close.focus()
  }

  private renderQuestion(question: Question, index: number) {
    const card = element('div', 'question-card')
    const wrapper = element('fieldset', 'field')
    const legend = element('legend', 'label')
    const heading = element('span', 'question-heading')
    heading.append(element('span', 'question-number', String(index + 1).padStart(2, '0')), element('span', 'question-title', question.titulo))
    if (question.obrigatoria) heading.append(element('span', 'required', ' *'))
    else heading.append(element('span', 'question-optional', 'Opcional'))
    legend.append(heading)
    wrapper.append(legend)

    if (question.tipo === 'TEXTO') {
      const input = element('textarea', 'textarea')
      input.name = question.id
      input.required = question.obrigatoria
      input.maxLength = 2000
      input.setAttribute('aria-label', question.titulo)
      wrapper.append(input)
    } else if (question.tipo === 'ESCOLHA_UNICA') {
      const choices = element('div', 'choices')
      for (const option of question.opcoes) {
        const label = element('label', 'choice')
        const input = document.createElement('input')
        input.type = 'radio'
        input.name = question.id
        input.value = option.id
        input.required = question.obrigatoria
        label.append(input, document.createTextNode(option.texto))
        choices.append(label)
      }
      wrapper.append(choices)
    } else {
      const scores = element('div', 'scores')
      for (let score = 0; score <= 10; score += 1) {
        const label = element('label', 'score')
        const input = document.createElement('input')
        input.type = 'radio'
        input.name = question.id
        input.value = String(score)
        input.required = question.obrigatoria
        label.append(input, element('span', '', String(score)))
        scores.append(label)
      }
      wrapper.append(scores)
    }
    card.append(wrapper)
    return card
  }

  private async submitSurvey(event: SubmitEvent, campaign: Extract<Campaign, { tipo: 'PESQUISA' }>, form: HTMLFormElement, status: HTMLElement, submit: HTMLButtonElement) {
    event.preventDefault()
    if (!form.reportValidity()) return
    const formData = new FormData(form)
    const respostas = campaign.conteudo.perguntas.flatMap(question => {
      const raw = formData.get(question.id)
      if (typeof raw !== 'string' || raw === '') return []
      const valor: AnswerValue = question.tipo === 'NOTA' ? Number(raw) : raw
      return [{ perguntaId: question.id, valor }]
    })
    const signature = JSON.stringify(respostas)
    if (!this.responseAttempt || this.responseAttempt.signature !== signature) this.responseAttempt = { signature, key: createId() }
    submit.disabled = true
    status.textContent = 'Enviando…'

    try {
      await this.authorizedRequest(`/entregas/${encodeURIComponent(campaign.entregaId)}/respostas`, {
        method: 'POST',
        headers: { 'Idempotency-Key': this.responseAttempt.key },
        body: JSON.stringify({ respostas })
      })
      if (this.currentCampaign?.entregaId !== campaign.entregaId) return
      form.replaceChildren(element('div', 'thanks', campaign.conteudo.agradecimento))
      this.broadcastCompletion(campaign.id)
      window.setTimeout(() => this.completeCampaign(campaign), 1800)
    } catch (error) {
      if (error instanceof HttpError && (error.status === 409 || error.status === 410)) {
        this.completeCampaign(campaign)
        return
      }
      status.textContent = 'Não foi possível enviar. Suas respostas foram mantidas; tente novamente.'
      submit.disabled = false
    }
  }

  private async submitInterest(campaign: Extract<Campaign, { tipo: 'AVISO' }>, button: HTMLButtonElement, status: HTMLElement) {
    const action = campaign.conteudo.botaoInteresse
    if (!action || button.disabled || this.currentCampaign?.entregaId !== campaign.entregaId) return
    if (!this.interestAttempt || this.interestAttempt.deliveryId !== campaign.entregaId || this.interestAttempt.actionId !== action.acaoId) {
      this.interestAttempt = { deliveryId: campaign.entregaId, actionId: action.acaoId, key: createId() }
    }

    const attempt = this.interestAttempt
    const originalLabel = action.texto
    button.disabled = true
    button.setAttribute('aria-busy', 'true')
    button.textContent = 'Enviando…'
    this.setInterestBusy(campaign.entregaId, true)
    status.className = 'status'
    status.textContent = 'Registrando seu interesse…'

    try {
      const payload = await this.authorizedRequest<unknown>(`/entregas/${encodeURIComponent(campaign.entregaId)}/interesse`, {
        method: 'POST',
        headers: { 'Idempotency-Key': attempt.key },
        body: JSON.stringify({ acaoId: action.acaoId })
      })
      parseInterestConfirmation(payload, campaign.id, action.acaoId)
      if (this.currentCampaign?.entregaId !== campaign.entregaId || this.interestAttempt !== attempt) return
      status.className = 'status success'
      status.textContent = action.mensagemSucesso
      button.textContent = 'Interesse registrado'
      button.removeAttribute('aria-busy')
      this.broadcastCompletion(campaign.id)
      window.setTimeout(() => this.completeCampaign(campaign), 1800)
    } catch (error) {
      if (this.currentCampaign?.entregaId !== campaign.entregaId || this.interestAttempt !== attempt) return
      if (error instanceof HttpError && (error.status === 403 || error.status === 410)) {
        status.className = 'status error'
        status.textContent = error.status === 410 ? 'Este aviso expirou e não pode mais receber interesse.' : 'Esta ação não está mais disponível.'
        button.textContent = originalLabel
        button.removeAttribute('aria-busy')
        window.setTimeout(() => this.completeCampaign(campaign), 1800)
        return
      }
      this.setInterestBusy(campaign.entregaId, false)
      status.className = 'status error'
      status.textContent = 'Não foi possível registrar. Verifique sua conexão e tente novamente.'
      button.textContent = 'Tentar novamente'
      button.removeAttribute('aria-busy')
      button.disabled = false
      button.focus()
    }
  }

  private createCloseButton(campaign: Campaign) {
    const button = element('button', 'close', '×')
    button.type = 'button'
    button.setAttribute('aria-label', 'Fechar')
    button.addEventListener('click', () => {
      if (this.interestSubmittingDeliveryId === campaign.entregaId) return
      void this.sendEvent(campaign, 'DISPENSOU', true)
      this.completeCampaign(campaign)
    })
    return button
  }

  private createDeferredButton(campaign: Campaign, text: string) {
    const button = element('button', 'button secondary pql-dismiss', text)
    button.type = 'button'
    button.addEventListener('click', () => {
      if (this.interestSubmittingDeliveryId === campaign.entregaId) return
      void this.sendEvent(campaign, 'DISPENSOU', true)
      this.completeCampaign(campaign)
    })
    return button
  }

  private setInterestBusy(deliveryId: string, busy: boolean) {
    this.interestSubmittingDeliveryId = busy ? deliveryId : null
    const close = this.host?.shadowRoot?.querySelector<HTMLButtonElement>('.close')
    if (close) close.disabled = busy
    const defer = this.host?.shadowRoot?.querySelector<HTMLButtonElement>('.pql-dismiss')
    if (defer) defer.disabled = busy
  }

  private activateKeyboard(container: HTMLElement, trapFocus: boolean, campaign: Campaign) {
    container.tabIndex = -1
    container.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        if (this.interestSubmittingDeliveryId === campaign.entregaId) return
        event.preventDefault()
        void this.sendEvent(campaign, 'DISPENSOU', true)
        this.completeCampaign(campaign)
        return
      }
      if (!trapFocus || event.key !== 'Tab') return
      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>('button:not([disabled]),a[href],input:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const activeElement = container.getRootNode() instanceof ShadowRoot ? (container.getRootNode() as ShadowRoot).activeElement : document.activeElement
      if (event.shiftKey && activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    })
  }

  private async sendEvent(campaign: Campaign, type: EventType, keepalive: boolean) {
    const keyName = `${campaign.entregaId}:${type}`
    const idempotencyKey = this.eventKeys.get(keyName) ?? createId()
    this.eventKeys.set(keyName, idempotencyKey)
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await this.authorizedRequest(`/entregas/${encodeURIComponent(campaign.entregaId)}/eventos`, {
          method: 'POST',
          headers: { 'Idempotency-Key': idempotencyKey },
          body: JSON.stringify({ tipo: type }),
          keepalive
        })
        return
      } catch (error) {
        if (error instanceof HttpError && [401, 403, 409, 410].includes(error.status)) return
        if (attempt < 2) await delay(400 * 2 ** attempt + Math.random() * 250)
      }
    }
  }

  private async authorizedRequest<T>(path: string, init: RequestInit): Promise<T> {
    const generation = this.generation
    await this.ensureSession(generation)
    try {
      return await this.request<T>(path, { ...init, headers: { ...this.authHeaders(), ...init.headers } })
    } catch (error) {
      if (!(error instanceof HttpError) || error.status !== 401 || generation !== this.generation) throw error
      this.session = null
      await this.ensureSession(generation, true)
      return this.request<T>(path, { ...init, headers: { ...this.authHeaders(), ...init.headers } })
    }
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const controller = new AbortController()
    this.controllers.add(controller)
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    try {
      const response = await fetch(`${API_URL}${path}`, {
        ...init,
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...init.headers },
        signal: controller.signal
      })
      if (!response.ok) throw new HttpError(response.status, parseRetryAfter(response.headers.get('Retry-After')))
      if (response.status === 204) return undefined as T
      return (await response.json()) as T
    } finally {
      window.clearTimeout(timeout)
      this.controllers.delete(controller)
    }
  }

  private authHeaders() {
    if (!this.session) throw new Error('PQLFlow SDK session is unavailable')
    return { Authorization: `Bearer ${this.session.token}` }
  }

  private schedulePoll(delayMs: number, generation: number) {
    this.clearPollTimer()
    if (document.visibilityState === 'hidden') return
    this.pollTimer = window.setTimeout(() => void this.poll(generation), delayMs)
  }

  private scheduleAfterFailure(error: unknown, generation: number) {
    this.failureCount += 1
    const retryAfter = error instanceof HttpError ? error.retryAfterMs : null
    const exponential = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(this.failureCount, 7))
    const jittered = exponential * (0.8 + Math.random() * 0.4)
    this.schedulePoll(retryAfter ?? jittered, generation)
  }

  private scheduleExpiry(campaign: Campaign, generation: number) {
    if (this.expiryTimer !== null) window.clearTimeout(this.expiryTimer)
    const expiresAt = Math.min(Date.parse(campaign.fimEm), Date.parse(campaign.entregaExpiresAt))
    const expiresIn = Math.max(0, expiresAt - this.serverNow())
    this.expiryTimer = window.setTimeout(
      () => {
        if (generation === this.generation && this.currentCampaign?.id === campaign.id) {
          this.closeCampaign(false)
          this.schedulePoll(0, generation)
        }
      },
      Math.min(expiresIn, 2_147_483_647)
    )
  }

  private completeCampaign(campaign: Campaign) {
    if (this.currentCampaign?.id !== campaign.id) return
    this.broadcastCompletion(campaign.id)
    this.closeCampaign(true)
    this.schedulePoll(DEFAULT_POLL_MS, this.generation)
  }

  private closeCampaign(restoreFocus: boolean) {
    if (this.expiryTimer !== null) window.clearTimeout(this.expiryTimer)
    this.expiryTimer = null
    this.host?.remove()
    this.host = null
    this.currentCampaign = null
    this.responseAttempt = null
    this.interestAttempt = null
    this.interestSubmittingDeliveryId = null
    if (restoreFocus && this.previousFocus?.isConnected) this.previousFocus.focus()
    this.previousFocus = null
  }

  private resetContext(clearConfiguration = true) {
    this.generation += 1
    this.clearPollTimer()
    if (this.expiryTimer !== null) window.clearTimeout(this.expiryTimer)
    this.expiryTimer = null
    for (const controller of this.controllers) controller.abort()
    this.controllers.clear()
    this.closeCampaign(true)
    this.channel?.close()
    this.channel = null
    this.session = null
    this.failureCount = 0
    this.serverOffsetMs = 0
    this.eventKeys.clear()
    if (clearConfiguration) {
      this.integrationId = null
      this.identityProvider = null
    }
  }

  private openChannel() {
    if (!this.integrationId || typeof BroadcastChannel === 'undefined') return
    this.channel?.close()
    this.channel = new BroadcastChannel(`${CHANNEL_PREFIX}${this.integrationId}`)
    this.channel.addEventListener('message', event => {
      const data = event.data as { type?: unknown; campaignId?: unknown }
      if (data?.type === 'completed' && typeof data.campaignId === 'string' && this.currentCampaign?.id === data.campaignId) {
        this.closeCampaign(true)
        this.schedulePoll(DEFAULT_POLL_MS, this.generation)
      }
    })
  }

  private broadcastCompletion(campaignId: string) {
    this.channel?.postMessage({ type: 'completed', campaignId })
  }

  private updateServerClock(serverTime: string) {
    this.serverOffsetMs = Date.parse(serverTime) - Date.now()
  }

  private serverNow() {
    return Date.now() + this.serverOffsetMs
  }

  private clearPollTimer() {
    if (this.pollTimer !== null) window.clearTimeout(this.pollTimer)
    this.pollTimer = null
  }

  private handleVisibilityChange = () => {
    if (!this.initialized) return
    this.clearPollTimer()
    if (document.visibilityState === 'visible') void this.start(this.generation)
  }

  private handleDeviceChange = () => {
    if (!this.initialized) return
    if (this.mobileViewport.matches && this.currentCampaign?.conteudo.exibirNoMobile === false) this.closeCampaign(true)
    this.clearPollTimer()
    if (document.visibilityState === 'visible') this.schedulePoll(0, this.generation)
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isInitOptions(value: unknown): value is InitOptions {
  return isRecord(value) && typeof value.integrationId === 'string' && value.integrationId.trim().length > 0 && typeof value.getIdentityToken === 'function'
}

function isIdentifyOptions(value: unknown): value is IdentifyOptions {
  return isRecord(value) && typeof value.getIdentityToken === 'function'
}

function parseSession(value: unknown): SdkSession {
  if (!isRecord(value) || typeof value.token !== 'string' || !isDate(value.expiresAt) || !isDate(value.serverTime) || !isPositiveNumber(value.pollIntervalSeconds)) {
    throw new Error('Invalid PQLFlow SDK session response')
  }
  return value as unknown as SdkSession
}

function parsePendingResponse(value: unknown): PendingResponse {
  if (!isRecord(value) || !Array.isArray(value.items) || value.items.length > 1 || !isDate(value.serverTime) || !isPositiveNumber(value.pollIntervalSeconds)) {
    throw new Error('Invalid PQLFlow pending campaigns response')
  }
  const items = value.items.map(parseCampaign)
  return { items, serverTime: value.serverTime as string, pollIntervalSeconds: value.pollIntervalSeconds as number }
}

function parseCampaign(value: unknown): Campaign {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    !Number.isInteger(value.version) ||
    !isDate(value.inicioEm) ||
    !isDate(value.fimEm) ||
    typeof value.entregaId !== 'string' ||
    !isDate(value.entregaExpiresAt) ||
    !isRecord(value.conteudo)
  ) {
    throw new Error('Invalid PQLFlow campaign')
  }
  if (value.tipo === 'AVISO' && isNoticeContent(value.conteudo)) {
    return { ...value, conteudo: { ...value.conteudo, botaoInteresse: value.conteudo.botaoInteresse ?? null } } as unknown as Campaign
  }
  if (value.tipo === 'PESQUISA' && isSurveyContent(value.conteudo)) return value as unknown as Campaign
  throw new Error('Invalid PQLFlow campaign content')
}

function hasTheme(value: Record<string, unknown>) {
  return (
    (value.exibirNoMobile === undefined || typeof value.exibirNoMobile === 'boolean') &&
    ['titulo', 'texto', 'corFundo', 'corTexto', 'corDestaque'].every(key => typeof value[key] === 'string')
  )
}

function isPresentationIcon(value: unknown): value is PresentationIcon | null {
  return value === null || value === 'CARTAO' || value === 'CALENDARIO' || value === 'ESCUDO' || value === 'PRESENTE' || value === 'INFORMACAO'
}

function isPresentationContent(value: unknown): value is PresentationContent {
  if (!isRecord(value) || !Array.isArray(value.cartoes) || value.cartoes.length > 4) return false
  const validText = (input: unknown, max: number) => typeof input === 'string' && input.trim().length > 0 && input.length <= max
  const validColor = (input: unknown) => typeof input === 'string' && /^#[0-9a-f]{6}$/i.test(input)
  const header = value.cabecalho
  const highlight = value.destaque
  if (
    header !== null &&
    (!isRecord(header) || !validText(header.legenda, 60) || !isPresentationIcon(header.icone) || !validColor(header.corInicial) || !validColor(header.corFinal))
  )
    return false
  if (
    highlight !== null &&
    (!isRecord(highlight) || !validText(highlight.titulo, 100) || !validText(highlight.texto, 400) || !isPresentationIcon(highlight.icone) || !validColor(highlight.cor))
  )
    return false
  if (
    !value.cartoes.every(
      card =>
        isRecord(card) && isNonEmptyString(card.id) && validText(card.titulo, 100) && validText(card.texto, 400) && isPresentationIcon(card.icone) && validColor(card.corFundo)
    )
  )
    return false
  if (new Set(value.cartoes.map(card => (card as { id: string }).id)).size !== value.cartoes.length) return false
  return (
    (value.textoFinal === null || validText(value.textoFinal, 400)) &&
    (value.botaoSecundario === null || validText(value.botaoSecundario, 60)) &&
    (value.botaoPrincipal === null || validText(value.botaoPrincipal, 60))
  )
}

function isNoticeContent(value: Record<string, unknown>): boolean {
  const image = value.imagem
  const button = value.botao
  const interest = value.botaoInteresse
  const hasInterest = isRecord(interest) && isNonEmptyString(interest.acaoId) && isNonEmptyString(interest.texto) && isNonEmptyString(interest.mensagemSucesso)
  return (
    hasTheme(value) &&
    (value.video == null ||
      (value.formato === 'MODAL' &&
        isRecord(value.video) &&
        value.video.provedor === 'YOUTUBE' &&
        typeof value.video.url === 'string' &&
        youtubeVideoId(value.video.url) !== null)) &&
    (value.formato === 'BANNER' || value.formato === 'MODAL') &&
    (image === null ||
      (isRecord(image) &&
        typeof image.url === 'string' &&
        typeof image.alt === 'string' &&
        (image.larguraPercentual === undefined || isImageWidthPercent(image.larguraPercentual)))) &&
    (button === null || (isRecord(button) && typeof button.url === 'string' && typeof button.texto === 'string')) &&
    (interest === undefined || interest === null || hasInterest) &&
    (value.apresentacao === undefined || value.apresentacao === null || isPresentationContent(value.apresentacao)) &&
    (!hasInterest || (value.formato === 'MODAL' && button === null))
  )
}

function parseInterestConfirmation(value: unknown, campaignId: string, actionId: string): InterestConfirmation {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.id) ||
    value.campanhaId !== campaignId ||
    !(value.idFilial === null || isNonEmptyString(value.idFilial)) ||
    value.acaoId !== actionId ||
    !isDate(value.receivedAt)
  ) {
    throw new Error('Invalid PQLFlow interest confirmation')
  }
  return value as unknown as InterestConfirmation
}

function isSurveyContent(value: Record<string, unknown>): boolean {
  return (
    hasTheme(value) &&
    value.formato === 'MODAL' &&
    typeof value.agradecimento === 'string' &&
    Array.isArray(value.perguntas) &&
    value.perguntas.length > 0 &&
    value.perguntas.every(isQuestion) &&
    (value.apresentacao === undefined || value.apresentacao === null || isPresentationContent(value.apresentacao))
  )
}

function isQuestion(value: unknown): value is Question {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.titulo !== 'string' || typeof value.obrigatoria !== 'boolean') return false
  if (value.tipo === 'TEXTO' || value.tipo === 'NOTA') return true
  return (
    value.tipo === 'ESCOLHA_UNICA' &&
    Array.isArray(value.opcoes) &&
    value.opcoes.length > 0 &&
    value.opcoes.every(option => isRecord(option) && typeof option.id === 'string' && typeof option.texto === 'string')
  )
}

function isDate(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value))
}

function isPositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function isHttpsUrl(value: string) {
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

function clampPoll(seconds: number) {
  return Math.min(300, Math.max(15, seconds)) * 1000
}

function parseRetryAfter(value: string | null) {
  if (!value) return null
  const seconds = Number(value)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)
  const date = Date.parse(value)
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : null
}

function createId() {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function delay(milliseconds: number) {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds))
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError'
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text) node.textContent = text
  return node
}

function createPresentationIcon(kind: PresentationIcon) {
  const shapes: Record<PresentationIcon, string[]> = {
    CARTAO: ['M3 6h18v12H3z', 'M3 10h18', 'M7 15h4'],
    CALENDARIO: ['M4 5h16v16H4z', 'M4 10h16', 'M8 3v4', 'M16 3v4', 'M8 14h3'],
    ESCUDO: ['M12 2l8 4v6c0 5-3 8-8 10-5-2-8-5-8-10V6z', 'M12 7v10'],
    PRESENTE: ['M3 10h18v11H3z', 'M2 7h20v3H2z', 'M12 7v14', 'M12 7c-4 0-6-1-6-3 0-3 5-2 6 3z', 'M12 7c4 0 6-1 6-3 0-3-5-2-6 3z'],
    INFORMACAO: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M12 11v5', 'M12 8h.01']
  }
  const icon = element('span', 'pql-detail-icon')
  icon.setAttribute('aria-hidden', 'true')
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24')
  for (const d of shapes[kind]) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', d)
    svg.append(path)
  }
  icon.append(svg)
  return icon
}

function createPresentationHeader(title: string, label: string, kind: 'notice' | 'survey', presentation?: PresentationContent | null) {
  if (presentation?.cabecalho === null) return null
  const config = presentation?.cabecalho
  const header = element('header', 'pql-header')
  if (config && /^#[0-9a-f]{6}$/i.test(config.corInicial)) header.style.setProperty('--pql-header-start', config.corInicial)
  if (config && /^#[0-9a-f]{6}$/i.test(config.corFinal)) header.style.setProperty('--pql-header-end', config.corFinal)
  const selectedIcon = config ? config.icone : kind === 'notice' ? 'CARTAO' : 'INFORMACAO'
  if (selectedIcon) {
    const icon = createPresentationIcon(selectedIcon)
    icon.classList.add('pql-header-icon')
    header.append(icon)
  }
  const copy = element('div', 'pql-header-copy')
  const titleElement = element('h2', 'title', title)
  titleElement.id = 'pql-campaign-title'
  copy.append(element('span', 'pql-eyebrow', config?.legenda || label), titleElement)
  header.append(copy)
  return header
}

function appendPresentationSections(main: HTMLElement, presentation?: PresentationContent | null) {
  if (!presentation) return
  if (presentation.destaque) {
    const highlight = element('section', 'pql-highlight')
    highlight.style.setProperty('--pql-highlight-color', presentation.destaque.cor)
    if (presentation.destaque.icone) highlight.append(createPresentationIcon(presentation.destaque.icone))
    const copy = element('div')
    copy.append(element('strong', '', presentation.destaque.titulo), element('p', '', presentation.destaque.texto))
    highlight.append(copy)
    main.append(highlight)
  }
  if (presentation.cartoes.length) {
    const grid = element('div', 'pql-details')
    for (const item of presentation.cartoes) {
      const card = element('section', 'pql-detail')
      card.style.backgroundColor = item.corFundo
      if (item.icone) card.append(createPresentationIcon(item.icone))
      const copy = element('div')
      copy.append(element('strong', '', item.titulo), element('p', '', item.texto))
      card.append(copy)
      grid.append(card)
    }
    main.append(grid)
  }
  if (presentation.textoFinal) main.append(element('p', 'pql-closing', presentation.textoFinal))
}

function createImage(content: ImageContent) {
  const image = element('img', 'image')
  image.src = content.url
  image.alt = content.alt
  if (isImageWidthPercent(content.larguraPercentual)) {
    image.style.width = `${content.larguraPercentual}%`
    image.style.marginInline = 'auto'
  }
  image.loading = 'lazy'
  image.referrerPolicy = 'no-referrer'
  image.addEventListener('error', () => image.remove(), { once: true })
  return image
}

function setTheme(node: HTMLElement, content: { corFundo: string; corTexto: string; corDestaque: string }) {
  if (/^#[0-9a-f]{6}$/i.test(content.corFundo)) node.style.setProperty('--pql-bg', content.corFundo)
  if (/^#[0-9a-f]{6}$/i.test(content.corTexto)) node.style.setProperty('--pql-text', content.corTexto)
  if (/^#[0-9a-f]{6}$/i.test(content.corDestaque)) node.style.setProperty('--pql-accent', content.corDestaque)
}

function normalizeQueuedCommand(value: unknown): Command | null {
  const command = Array.from(value as ArrayLike<unknown>)
  if (command[0] === 'init' && isInitOptions(command[1])) return ['init', command[1]]
  if (command[0] === 'identify' && isIdentifyOptions(command[1])) return ['identify', command[1]]
  if (command[0] === 'destroy') return ['destroy']
  return null
}

function install() {
  const queued = Array.isArray(window.PQLFlow?.q) ? [...window.PQLFlow.q] : []
  const runtime = window.__PQLFLOW_SDK_INSTANCE__ ?? new PQLFlowRuntime()
  window.__PQLFLOW_SDK_INSTANCE__ = runtime
  const dispatcher: PQLFlowDispatcher = (...command) => runtime.dispatch(command[0], command[1])
  dispatcher.version = __PQLFLOW_SDK_VERSION__
  window.PQLFlow = dispatcher
  for (const item of queued) {
    const command = normalizeQueuedCommand(item)
    if (command) dispatcher(...command)
  }
}

try {
  install()
} catch {
  // O SDK nunca deve interromper a aplicação hospedeira.
}
